// Stack-slot geometry shared by every web modal so they pile into ONE coherent iOS-sheet-like
// window, whichever engine renders them: the JS modals (components/ModalPortal.tsx) and the
// navigation modals (via hooks/use-modal-recede-style.ts). Keeping the geometry in one place stops
// the two engines from drifting apart.
//
// `depth` is the distance from the front (0 = front-most). The front sits slightly low
// (FRONT_DOWN_PEEK); the one behind (depth 1) is nudged up + scaled down so it peeks above the
// front; from depth 2 on it slides back DOWN to the front slot — hidden behind the opaque front — so
// only two modals are ever visible (the oldest "descends" out of view as a new one opens).
export const FRONT_DOWN_PEEK = 15;
export const STACK_OFFSET = 25;
export const STACK_SCALE_STEP = 0.05;

// A stacked modal (the 2nd and beyond) enters/exits with a small upward slide instead of a scale
// pop: it starts this many px below its resting slot and rises into place on open (and back down on
// close). The very first modal (opening over the app) keeps the scale. Shared by both web engines
// (components/ModalPortal.tsx and hooks/use-modal-card-style-interpolator.ts) so they match.
export const STACK_ENTER_TRANSLATE_Y = 20;

// The centered web dialog box, shared by EVERY web modal (navigation modals via ModalDialogFrame and
// the JS modals in components/ModalPortal.tsx) so they are pixel-identical.
export const WEB_DIALOG_WIDTH = 700;
export const WEB_DIALOG_HEIGHT = "85%";
export const WEB_DIALOG_MAX_HEIGHT = 900;
// Shared open/close timing for the web dialog animation.
export const WEB_DIALOG_ANIMATION_DURATION = 300;

export type ModalStackPosition = { depthFromTop: number; stacked: boolean };

/**
 * Position of one modal in the SINGLE conceptual stack that both engines feed (navigation modals sit
 * below the JS modals). `indexFromBottom` counts the visible modals below it; `total` is the visible
 * modal count. Returns how deep it is from the top (drives the recede slot) and whether anything is
 * below it (drives slide-vs-scale on enter). Centralises the rule so the two engines never diverge.
 */
export const getModalStackPosition = (
    indexFromBottom: number,
    total: number,
): ModalStackPosition => ({
    depthFromTop: Math.max(0, total - 1 - indexFromBottom),
    stacked: indexFromBottom > 0,
});

export type ModalStackSlot = { translateY: number; scale: number };

export const getModalStackSlot = (depth: number): ModalStackSlot => {
    if (depth <= 1) {
        // front (+FRONT_DOWN_PEEK) → behind (-STACK_OFFSET)
        return {
            translateY: FRONT_DOWN_PEEK + depth * (-STACK_OFFSET - FRONT_DOWN_PEEK),
            scale: 1 - depth * STACK_SCALE_STEP,
        };
    }
    // behind (-STACK_OFFSET) → back to the front slot (+FRONT_DOWN_PEEK), hidden behind the front
    const t = Math.min(depth - 1, 1);
    return {
        translateY: -STACK_OFFSET + t * (FRONT_DOWN_PEEK + STACK_OFFSET),
        scale: 1 - STACK_SCALE_STEP,
    };
};
